import { computed, effect, inject, Injectable, OnDestroy, signal } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import {
    DropListRegistry,
    DropListGroupId,
    DropListRegistryItem,
    DRAG_AND_DROP_CONFIG,
    OutOfBoundsStrategy,
    DragAndDropConfig,
} from "./drag-and-drop.interface";
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragEnter,
    CdkDragMove,
    CdkDragStart,
    CdkDropList,
    moveItemInArray,
    transferArrayItem,
} from "@angular/cdk/drag-drop";
import { Subject } from "rxjs";
import { NoopStrategy } from "./out-of-bounds/noop-strategy";
import { HighlightOriginStrategy } from "./out-of-bounds/highlight-origin-strategy";

// Constant value to represent out-of-bounds state, must be distinct from any drop list ID
const DRAG_OUT_OF_BOUNDS = "DRAG OUT OF BOUNDS";

@Injectable({ providedIn: "root" })
export class DragAndDropService implements OnDestroy {
    private config: DragAndDropConfig = inject(DRAG_AND_DROP_CONFIG, { optional: true });
    private strategy: OutOfBoundsStrategy = this.config?.outOfBoundsStrategy || new NoopStrategy();
    private proximityThreshold: number = this.config?.proximityThreshold || 30;
    private readonly document = inject(DOCUMENT);

    readonly dragStarted = new Subject<CdkDragStart<any>>();
    readonly dragCancelled = new Subject<void>();
    readonly dragDropped = new Subject<CdkDragDrop<any>>();
    readonly dragEnded = new Subject<void>();
    readonly dragOverList = new Subject<CdkDropList<any>>();

    readonly registeredDropLists = signal<DropListRegistry>(new Map());
    readonly drag = signal<CdkDrag<any> | null>(null);
    readonly isDragging = computed<boolean>(() => {
        const drag = this.drag();
        return !!drag;
    });
    private originDropList = signal<CdkDropList<any> | null>(null);
    private activeDropList = signal<CdkDropList<any> | null>(null);
    private pointerPosition = signal<{ x: number; y: number }>(null);
    private draggingOverList = computed<string | null>(() => {
        const pointerPosition = this.pointerPosition();
        const activeDropList = this.activeDropList();

        if (!pointerPosition || !activeDropList) return null;

        const near = this.isPointerNear(activeDropList.element.nativeElement.getBoundingClientRect(), this.proximityThreshold, pointerPosition);
        return near ? activeDropList.id : DRAG_OUT_OF_BOUNDS;
    });

    private isOutOfBounds = false;

    constructor() {
        effect(() => {
            const draggingOverList = this.draggingOverList();
            const isOutOfBounds = this.isOutOfBounds;

            if (!draggingOverList) return;

            if (!isOutOfBounds && draggingOverList === DRAG_OUT_OF_BOUNDS) {
                // Moving out of bounds
                this.strategy.handleOutOfBounds(this.drag(), this.originDropList(), this.activeDropList());
                this.isOutOfBounds = true;
            } else if (isOutOfBounds && draggingOverList !== DRAG_OUT_OF_BOUNDS) {
                // Moving back in bounds
                this.strategy.handleReturnInBounds();
                this.isOutOfBounds = false;
            }
        });
    }

    registerDropList(dropListGroupId: DropListGroupId, dropList: CdkDropList<any>) {
        const current = this.registeredDropLists();
        let item: DropListRegistryItem = current.get(dropListGroupId);
        if (!item) item = { connectedDropLists: [] };
        if (item.connectedDropLists.includes(dropList)) return;

        // The drop list option hasAnchor is mandatory for the PlaceholderAtOriginStrategy to work,
        // so it is set automatically to avoid relying on client code to set it correctly
        // Same for orientation which needs to be "mixed" to avoid visual glitches when placeholder
        // is removed from the page flow. Angular CDK transforms approach to list sorting does not
        // work well with DOM changes mid-drag.
        if (this.strategy instanceof HighlightOriginStrategy) {
            dropList.hasAnchor = true;
            dropList.orientation = "mixed";
        }

        item.connectedDropLists.push(dropList);
        current.set(dropListGroupId, item);
        this.registeredDropLists.set(current);
    }

    onDragStarted(event: CdkDragStart<any>) {
        this.drag.set(event.source);
        const originDropList = event.source.dropContainer;
        this.originDropList.set(originDropList);
        this.activeDropList.set(originDropList);
        this.isOutOfBounds = false;
        this.document.documentElement.classList.add("is-dragging");
        this.dragStarted.next(event);
    }

    onDragMoved(event: CdkDragMove<any>) {
        // Pointer position is irrelevant if strategy does not use it. Avoid unnecessary updates to improve performance.
        if (this.strategy instanceof NoopStrategy) return;
        this.pointerPosition.set(event.pointerPosition);
    }

    onDragEnded() {
        this.drag.set(null);
        this.pointerPosition.set(null);
        this.originDropList.set(null);
        this.activeDropList.set(null);
        this.isOutOfBounds = false;
        this.document.documentElement.classList.remove("is-dragging");

        this.strategy.handleDragEnd();
        this.dragEnded.next();
    }

    onDropListEntered(event: CdkDragEnter<any>) {
        this.activeDropList.set(event.container);
        this.dragOverList.next(event.container);
    }

    onDropListDropped(event: CdkDragDrop<any>) {
        // Dropping out of bounds
        if (!event.isPointerOverContainer) {
            this.cancelDrag(event);
            return;
        }

        // Dropping into the same container when sorting is disabled or drop list has no data
        if (event.previousContainer === event.container && (event.container.sortingDisabled || !event.container.data)) {
            this.cancelDrag(event);
            return;
        }

        if (event.container.data && event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else if (event.previousContainer.data && event.container.data) {
            transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
        }

        this.onDragEnded();
        this.dragDropped.next(event);
    }

    private cancelDrag(event: CdkDragDrop<any>) {
        event.item.reset();
        this.onDragEnded();
        this.dragCancelled.next();
    }

    private isPointerNear(rect: DOMRect, threshold: number, pointer: { x: number; y: number }): boolean {
        const { top, right, bottom, left } = rect;

        return pointer.y > top - threshold && pointer.y < bottom + threshold && pointer.x > left - threshold && pointer.x < right + threshold;
    }

    ngOnDestroy(): void {
        this.dragStarted.complete();
        this.dragCancelled.complete();
        this.dragDropped.complete();
        this.dragEnded.complete();
        this.dragOverList.complete();
    }
}
